/**
 * Codec Group editor (tabbed modal).
 *
 * Replaces the previous hand-rolled modal in CodecGroupsPage. Two tabs:
 * Basics (name/description/DTMF/ptime/flags) and Codecs (selection + priority).
 * The form is validated with a UI-shaped zod schema; the payload is revalidated
 * with the core CodecGroup schemas at submit (the API is the source of truth).
 */

import { useEffect, useMemo, useState } from 'react';
import { useForm, type UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Settings, Music } from 'lucide-react';
import { z } from 'zod';
import { useQueryClient } from '@tanstack/react-query';
import type { Sip } from '@sbclite/core';
import {
  CodecGroupCreateInputSchema,
  CodecGroupUpdateInputSchema,
  type CodecGroupCreateInput,
  type CodecGroupUpdateInput,
} from '@sbclite/core/schemas';
import { api } from '../../services/api';
import { ResourceEditor, firstTabWithError, describeSubmitError, type ResourceEditorTab } from '../common';
import { INSTALLED_CODEC_OPTIONS, getCodecTypeIcon, buildCodecRows } from './codecOptions';

type CodecGroupInfo = Sip.CodecGroupInfo;

const codecGroupFormSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string(),
  dtmfMode: z.enum(['rfc2833', 'sip-info', 'inband']),
  ptime: z.string(),
  enabled: z.boolean(),
  isDefault: z.boolean(),
  selectedCodecs: z.array(z.string()),
});

type CodecGroupFormData = z.infer<typeof codecGroupFormSchema>;

function buildDefaultValues(group?: CodecGroupInfo | null): CodecGroupFormData {
  return {
    name: group?.name ?? '',
    description: group?.description ?? '',
    dtmfMode: group?.dtmfMode ?? 'rfc2833',
    ptime: group?.ptime != null ? String(group.ptime) : '',
    enabled: group?.enabled ?? true,
    isDefault: group?.isDefault ?? false,
    selectedCodecs: group?.codecs
      ? [...group.codecs]
          .filter((codec) => codec.enabled !== false)
          .sort((a, b) => a.priority - b.priority)
          .map((codec) => codec.codecName)
      : [],
  };
}

interface CodecGroupEditorProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  editingGroup?: CodecGroupInfo | null;
}

export function CodecGroupEditor({ isOpen, onClose, onSuccess, editingGroup }: CodecGroupEditorProps) {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('basics');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isEditMode = Boolean(editingGroup);

  const form = useForm<CodecGroupFormData>({
    resolver: zodResolver(codecGroupFormSchema),
    defaultValues: buildDefaultValues(editingGroup),
  });

  useEffect(() => {
    if (!isOpen) return;
    form.reset(buildDefaultValues(editingGroup));
    setActiveTab('basics');
    setError(null);
  }, [isOpen, editingGroup, form]);

  const onSubmit = async (data: CodecGroupFormData) => {
    setIsSaving(true);
    setError(null);
    try {
      const codecs = buildCodecRows(data.selectedCodecs, editingGroup?.codecs);
      const ptimeValue = data.ptime.trim() === '' ? (isEditMode ? null : undefined) : Number(data.ptime);
      const codecGroupData = {
        name: data.name,
        description: data.description || undefined,
        enabled: data.enabled,
        codecs,
        dtmfMode: data.dtmfMode,
        ptime: ptimeValue,
        isDefault: data.isDefault,
      };

      if (editingGroup) {
        const parsedUpdate = CodecGroupUpdateInputSchema.parse(codecGroupData) as CodecGroupUpdateInput;
        await api.put(`/api/codec-groups/${editingGroup.id}`, parsedUpdate);
      } else {
        const parsedCreate = CodecGroupCreateInputSchema.parse(codecGroupData) as CodecGroupCreateInput;
        await api.post('/api/codec-groups', parsedCreate);
      }

      queryClient.invalidateQueries({ queryKey: ['codec-groups'] });
      onSuccess?.();
      onClose();
      form.reset(buildDefaultValues(null));
      setActiveTab('basics');
    } catch (err) {
      setError(describeSubmitError(err, `Failed to ${isEditMode ? 'update' : 'create'} codec group`));
    } finally {
      setIsSaving(false);
    }
  };

  const tabs: ResourceEditorTab[] = useMemo(
    () => [
      {
        id: 'basics',
        label: 'Basics',
        icon: Settings,
        fields: ['name', 'description', 'dtmfMode', 'ptime', 'enabled', 'isDefault'],
        render: () => <BasicsTab form={form} />,
      },
      {
        id: 'codecs',
        label: 'Codecs',
        icon: Music,
        fields: ['selectedCodecs'],
        render: () => <CodecsTab form={form} />,
      },
    ],
    [form]
  );

  const onInvalid = (errors: typeof form.formState.errors) => {
    const target = firstTabWithError(errors, tabs);
    if (target) setActiveTab(target);
  };

  return (
    <ResourceEditor
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? 'Edit Codec Group' : 'Create Codec Group'}
      subtitle={isEditMode ? 'Update codec selection and group settings' : 'Configure a new codec group for SIP media'}
      headerBanner={
        error ? (
          <div className="bg-red-50 px-6 py-3">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        ) : undefined
      }
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      errors={form.formState.errors}
      onSubmit={form.handleSubmit(onSubmit, onInvalid)}
      onCancel={onClose}
      isSaving={isSaving}
      saveLabel={isEditMode ? 'Save Changes' : 'Create Codec Group'}
      savingLabel={isEditMode ? 'Saving...' : 'Creating...'}
    />
  );
}

function BasicsTab({ form }: { form: UseFormReturn<CodecGroupFormData> }) {
  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
        <input
          type="text"
          {...form.register('name')}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="e.g., Teams Audio Codecs"
        />
        {form.formState.errors.name && (
          <p className="mt-1 text-sm text-red-600">{form.formState.errors.name.message}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
        <textarea
          {...form.register('description')}
          rows={2}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="Optional description"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">DTMF Mode</label>
          <select
            {...form.register('dtmfMode')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="rfc2833">rfc2833</option>
            <option value="sip-info">sip-info</option>
            <option value="inband">inband</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Packetization (ptime ms)</label>
          <input
            type="number"
            min={1}
            step={1}
            {...form.register('ptime')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Optional"
          />
        </div>
      </div>

      <div className="flex items-center">
        <input
          type="checkbox"
          id="codec-group-enabled"
          {...form.register('enabled')}
          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
        />
        <label htmlFor="codec-group-enabled" className="ml-2 block text-sm text-gray-700">
          Enable this codec group
        </label>
      </div>

      <div className="flex items-center">
        <input
          type="checkbox"
          id="codec-group-default"
          {...form.register('isDefault')}
          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
        />
        <label htmlFor="codec-group-default" className="ml-2 block text-sm text-gray-700">
          Mark as default codec group
        </label>
      </div>
    </div>
  );
}

function CodecsTab({ form }: { form: UseFormReturn<CodecGroupFormData> }) {
  const selectedCodecs = form.watch('selectedCodecs');

  const toggleCodec = (codecName: string) => {
    const current = form.getValues('selectedCodecs');
    const next = current.includes(codecName)
      ? current.filter((name) => name !== codecName)
      : [...current, codecName];
    form.setValue('selectedCodecs', next, { shouldDirty: true });
  };

  const moveCodec = (codecName: string, direction: 'up' | 'down') => {
    const current = form.getValues('selectedCodecs');
    const index = current.indexOf(codecName);
    if (index === -1) return;
    const nextIndex = direction === 'up' ? index - 1 : index + 1;
    if (nextIndex < 0 || nextIndex >= current.length) return;
    const copy = [...current];
    const [codec] = copy.splice(index, 1);
    copy.splice(nextIndex, 0, codec);
    form.setValue('selectedCodecs', copy, { shouldDirty: true });
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {INSTALLED_CODEC_OPTIONS.map((codec) => {
          const checked = selectedCodecs.includes(codec.name);
          const CodecIcon = getCodecTypeIcon(codec.codecType);
          return (
            <label
              key={codec.name}
              className={`flex items-center justify-between rounded-lg border px-3 py-2 cursor-pointer ${
                checked ? 'border-blue-300 bg-blue-50' : 'border-gray-200 bg-white'
              }`}
            >
              <span className="flex items-center gap-2 text-sm text-gray-900">
                <CodecIcon className="w-4 h-4" />
                {codec.name}
              </span>
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggleCodec(codec.name)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
            </label>
          );
        })}
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
        <p className="text-sm font-medium text-gray-700 mb-2">Selected codec priority (top to bottom)</p>
        {selectedCodecs.length === 0 ? (
          <p className="text-sm text-gray-600">
            No codecs selected. This group will block codec negotiation for peers that use it.
          </p>
        ) : (
          <div className="space-y-2">
            {selectedCodecs.map((codecName, index) => (
              <div key={codecName} className="flex items-center justify-between rounded border border-gray-200 bg-white px-3 py-2">
                <span className="text-sm text-gray-900">{index + 1}. {codecName}</span>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => moveCodec(codecName, 'up')}
                    className="px-2 py-1 text-xs border border-gray-300 rounded disabled:opacity-40"
                    disabled={index === 0}
                  >
                    Up
                  </button>
                  <button
                    type="button"
                    onClick={() => moveCodec(codecName, 'down')}
                    className="px-2 py-1 text-xs border border-gray-300 rounded disabled:opacity-40"
                    disabled={index === selectedCodecs.length - 1}
                  >
                    Down
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default CodecGroupEditor;
